import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { useNavigate, type UseNavigateResult } from '@tanstack/react-router';
import { useState } from 'react';
import { ASSET_ROUTE_URL } from '../../../constants/routes';
import { ProductType } from '../../../hooks/useProducts';
import { useTailwindBreakpoint } from '../../../hooks/useTailwindBreakpoint';
import { cn } from '../../../lib/classNameHelpers';
import { toLowerCase } from '../../../lib/stringHelpers';
import { orderTypeTooltip, proOrderSelectTooltip } from '../../../lib/tooltips';
import { Button } from '../../catalyst-ui-kit/button';
import { SelectOption, SimpleSelect } from '../../SimpleSelect/SimpleSelect';
import { Tooltip } from '../../Tooltip/Tooltip';
import { TradeFormHeader } from './TradeFormHeader';
import { useTradeFormStore } from './TradeFormState';

const PRO_ORDER_OPTIONS = [
  { value: 'sl-limit', label: 'SL Limit' },
  { value: 'sl-market', label: 'SL Market' },
  { value: 'tp-limit', label: 'TP Limit' },
  { value: 'tp-market', label: 'TP Market' },
] as const;

export const TradeFormOrderTypeUi: React.FC<{
  navigate: UseNavigateResult<typeof ASSET_ROUTE_URL>;
  product: ProductType;
}> = ({ navigate, product }) => {
  const isLg = useTailwindBreakpoint('lg');
  const orderType = useTradeFormStore((x) => x.orderType);
  const setOrderType = useTradeFormStore((x) => x.setOrderType);

  // Placeholder until pro-order options are in TradeFormState.
  const [proOrder, setProOrder] = useState<SelectOption>();

  const handleOrderTypeChange = (type: typeof orderType) => {
    setOrderType(type, () => {
      setProOrder(undefined);
      void navigate({ search: (prev) => ({ ...prev, orderType: toLowerCase(type) }) });
    });
  };

  return (
    <div role="radiogroup" aria-label="Order type selection" className="flex justify-between">
      <div className="flex gap-3">
        <Button
          text
          size="xs"
          role="radio"
          color="secondary"
          aria-checked={orderType === 'MARKET'}
          onClick={() => handleOrderTypeChange('MARKET')}
          className={cn({ 'text-contentPrimary': !proOrder && orderType === 'MARKET' })}
        >
          Market
        </Button>
        <Button
          text
          size="xs"
          role="radio"
          color="secondary"
          aria-checked={orderType === 'LIMIT'}
          onClick={() => handleOrderTypeChange('LIMIT')}
          className={cn({ 'text-contentPrimary': !proOrder && orderType === 'LIMIT' })}
        >
          Limit
        </Button>
        <Tooltip content={proOrderSelectTooltip()}>
          <SimpleSelect
            options={PRO_ORDER_OPTIONS}
            selected={proOrder}
            placeholder="Pro Order"
            onChange={(value) => setProOrder(value)}
            className={cn(
              'border-none text-contentSecondary enabled:hover:text-contentPrimary w-full text-nowrap p-0 text-xs font-medium',
              { 'text-contentPrimary': Boolean(proOrder) }
            )}
            disabled
          />
        </Tooltip>
      </div>
      {!isLg && <TradeFormHeader product={product} />}
      {isLg && (
        <Tooltip className="ml-auto" content={orderTypeTooltip(orderType)}>
          <InformationCircleIcon className="w-4 text-contentSecondary" />
        </Tooltip>
      )}
    </div>
  );
};

export const TradeFormOrderType: React.FC<{ product: ProductType }> = ({ product }) => {
  const navigate = useNavigate({ from: ASSET_ROUTE_URL });
  return <TradeFormOrderTypeUi navigate={navigate} product={product} />;
};
